import { useState, useEffect } from "react";
import { Button, Table, Popconfirm } from "@/frontend/components/common";
import { matchTeamService } from "@/frontend/services";
import type { PaginationRequestDTO, MatchTeamResponseDTO } from "@/shared/dto";
import { formatDate } from "@/shared/utils";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { usePagination, useDelete } from "@/frontend/hooks";
import { Select, DatePicker, Input } from "antd";

const { RangePicker } = DatePicker;

const initialPagination: TablePaginationConfig = {
  pageSizeOptions: ["7","10", "20", "50", "100"],
  showSizeChanger: true,
  showTotal: (total) => `Total: ${total} registros`,
};

const initialParams: PaginationRequestDTO = { page: 1, pageSize: 7 };

export function MatchTeamsListPage() {
  const navigate = useNavigate();
  const [filterName, setFilterName] = useState<string>("");
  const [filterDisciplineId, setFilterDisciplineId] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [disciplines, setDisciplines] = useState<{ id: string; name: string }[]>([]);

  const { data, loading, pagination, loadData } = usePagination<MatchTeamResponseDTO>({
    fetchFn: matchTeamService.getPaginate,
    initialParams,
    initialPagination,
  });

  useEffect(() => {
    matchTeamService.getPaginate({ page: 1, pageSize: 100 }).then((res) => {
      if (res.success && res.data) {
        const seen = new Map<string, string>();
        for (const t of res.data.data) {
          if (t.disciplineId && t.disciplineName && !seen.has(t.disciplineId)) {
            seen.set(t.disciplineId, t.disciplineName);
          }
        }
        setDisciplines([...seen.entries()].map(([id, name]) => ({ id, name })));
      }
    });
  }, []);

  function applyFilters(name = filterName, disciplineId = filterDisciplineId, range = dateRange) {
    const trimmedName = name.trim();

    loadData({
      page: 1,
      pageSize: 7,
      name: trimmedName || undefined,
      disciplineId,
      createdFrom: range?.[0],
      createdTo: range?.[1],
    });
  }

  const { handleDelete, showConfirm, confirmTitle, confirmDescription } = useDelete({
    deleteFn: matchTeamService.delete,
    loadData,
    successMessage: "Equipo eliminado exitosamente",
    errorMessage: "Error al eliminar equipo",
    showConfirm: true,
    confirmTitle: "¿Eliminar equipo?",
    confirmDescription: "Esta acción eliminará el equipo permanentemente. ¿Estás seguro?",
  });

  const columns: ColumnsType<MatchTeamResponseDTO> = [
    {
      title: "Escudo",
      dataIndex: "imageUrl",
      key: "imageUrl",
      width: 70,
      render: (val: string | null) =>
        val ? (
          <img
            src={val}
            alt="Escudo"
            className="w-8 h-8 object-contain rounded border border-gray-200 p-0.5"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Deporte",
      dataIndex: "disciplineName",
      key: "disciplineName",
      render: (val: string | null) => val || "-",
    },
    {
      title: "Categoría",
      dataIndex: "subCategoryNames",
      key: "subCategoryNames",
      render: (val: string[]) => val?.length ? val.join(", ") : "-",
    },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
      render: (val: string | null) => val || "-",
    },
    {
      title: "Fecha de Creación",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: Date) => formatDate(date),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 150,
      render: (_: unknown, record: MatchTeamResponseDTO) => (
        <div className="flex gap-2">
          <Button
            type="link"
            icon={<FaEdit />}
            onClick={() => navigate(`/match-teams/edit/${record.id}`)}
          >
            Editar
          </Button>
          {showConfirm ? (
            <Popconfirm
              title={confirmTitle}
              description={confirmDescription}
              onConfirm={() => handleDelete(record.id)}
              okText="Sí, eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" danger icon={<FaTrash />}>
                Eliminar
              </Button>
            </Popconfirm>
          ) : (
            <Button
              type="link"
              danger
              icon={<FaTrash />}
              onClick={() => handleDelete(record.id)}
            >
              Eliminar
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Gestión de Equipos</h2>
          <p className="text-gray-600 mt-1">
            Administra los equipos vinculados a cada disciplina y categoría
          </p>
        </div>
        <Button
          type="primary"
          icon={<FaPlus />}
          onClick={() => navigate("/match-teams/create")}
        >
          Crear Equipo
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <Input.Search
          placeholder="Buscar equipo por nombre"
          allowClear
          value={filterName}
          onChange={(event) => {
            const value = event.target.value;
            setFilterName(value);
            if (!value) applyFilters("", filterDisciplineId, dateRange);
          }}
          onSearch={(value) => {
            setFilterName(value);
            applyFilters(value, filterDisciplineId, dateRange);
          }}
          style={{ width: 260 }}
        />
        <Select
          placeholder="Deporte / Disciplina"
          allowClear
          showSearch
          value={filterDisciplineId}
          onChange={(val) => { setFilterDisciplineId(val); applyFilters(filterName, val, dateRange); }}
          options={disciplines.map((d) => ({ label: d.name, value: d.id }))}
          style={{ width: 220 }}
          filterOption={(input, opt) =>
            String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
        <RangePicker
          placeholder={["Fecha inicio", "Fecha fin"]}
          onChange={(_, dateStrings) => {
            const range = dateStrings && dateStrings[0] && dateStrings[1]
              ? [dateStrings[0], dateStrings[1]] as [string, string]
              : null;
            setDateRange(range);
            applyFilters(filterName, filterDisciplineId, range);
          }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={pagination}
      />
    </div>
  );
}
